import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  Typography,
  Box,
  Tabs,
  Tab,
  IconButton,
  InputAdornment,
  CircularProgress,
  Alert,
  Divider,
  Snackbar
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import PersonIcon from '@mui/icons-material/Person';
import LockIcon from '@mui/icons-material/Lock';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import theme from '../../theme';
import FormInput from '../../SystemDesign/formInput';
import { useAuth } from '../../context/AuthContext';
import { GoogleLogin } from '@react-oauth/google';
import { useTranslation } from 'react-i18next';
import { isNativeIosApp } from '../../utils/nativePlatform';

// Tab panel component
function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`auth-tabpanel-${index}`}
      aria-labelledby={`auth-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index) {
  return {
    id: `auth-tab-${index}`,
    'aria-controls': `auth-tabpanel-${index}`,
  };
}

const AuthModal = ({ open, onClose, userType = 'client', onAuthSuccess }) => {
  const { t } = useTranslation();
  const nativeIosApp = isNativeIosApp();
  const professionalRegistrationAllowed = !nativeIosApp || userType === 'client';

  // Validation schemas - moved inside component to use translations
  const loginSchema = Yup.object().shape({
    email: Yup.string()
      .email(t('auth.validation.email_invalid'))
      .required(t('auth.validation.email_required')),
    password: Yup.string()
      .required(t('auth.validation.password_required'))
  });

  const registerSchema = Yup.object().shape({
    first_name: Yup.string()
      .required(t('auth.validation.first_name_required'))
      .min(2, t('auth.validation.first_name_short')),
    last_name: Yup.string()
      .required(t('auth.validation.last_name_required'))
      .min(2, t('auth.validation.last_name_short')),
    email: Yup.string()
      .email(t('auth.validation.email_invalid'))
      .required(t('auth.validation.email_required')),
    phone: Yup.string()
      .matches(/^[+]?[0-9]{1,15}$/, t('auth.validation.phone_invalid'))
      .required(t('auth.validation.phone_required')),
    password: Yup.string()
      .required(t('auth.validation.password_required'))
      .min(8, t('auth.validation.password_min')),
    password_confirm: Yup.string()
      .required(t('auth.validation.password_confirm_required'))
      .oneOf([Yup.ref('password')], t('auth.validation.passwords_mismatch'))
  });

  // Tab state
  const [tabValue, setTabValue] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const hasGoogleClientId = Boolean((process.env.REACT_APP_GOOGLE_CLIENT_ID || '').trim());
  const shouldShowGoogleLogin = hasGoogleClientId && !nativeIosApp;

  useEffect(() => {
    if (!professionalRegistrationAllowed && tabValue !== 0) {
      setTabValue(0);
    }
  }, [professionalRegistrationAllowed, tabValue]);

  // Notification state
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'info' // 'success', 'info', 'warning', 'error'
  });

  const REG_SUCCESS_KEY = 'auth_registration_success_toast';

  // Handle closing the notification
  const handleCloseNotification = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setNotification(prev => ({ ...prev, open: false }));
  };

  useEffect(() => {
    if (!open) return;
    try {
      const raw = localStorage.getItem(REG_SUCCESS_KEY);
      if (raw) {
        localStorage.removeItem(REG_SUCCESS_KEY);
        setNotification({
          open: true,
          message: raw,
          severity: 'success'
        });
      }
    } catch (e) {
      // ignore
    }
  }, [open]);

  // Error states
  const [loginError, setLoginError] = useState('');
  const [registerError, setRegisterError] = useState('');

  // Get auth context
  const { user, login, googleLogin, googleLink, register, loading: authLoading, error: authError } = useAuth();

  // Initial form values
  const loginInitialValues = {
    email: '',
    password: ''
  };

  const registerInitialValues = {
    email: '',
    password: '',
    password_confirm: '',
    first_name: '',
    last_name: '',
    phone: ''
  };

  // Combined loading state
  const isLoading = authLoading;

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    setLoginError('');
    setRegisterError('');
  };

  const navigate = useNavigate();

  const extractBackendErrorMessage = (err) => {
    const data = err?.data || err?.response?.data;
    if (!data) return '';

    if (typeof data === 'string') return data;
    if (typeof data?.detail === 'string' && data.detail) return data.detail;

    const nonField = data?.non_field_errors;
    if (Array.isArray(nonField) && nonField.length) return String(nonField[0]);

    if (Array.isArray(data) && data.length) return String(data[0]);

    if (typeof data === 'object') {
      const flattened = Object.values(data).flat();
      const first = flattened.find((v) => typeof v === 'string' && v.trim());
      if (first) return first;
      if (flattened.length) return String(flattened[0]);
    }

    return '';
  };

  // Handle login submission
  const handleLogin = async (values, { setSubmitting, resetForm }) => {
    setLoginError('');

    try {
      const loginRequest = {
        email: values.email,
        password: values.password
      };

      // Use the login function from AuthContext
      const userData = await login(loginRequest, userType);

      // Handle successful login
      resetForm();
      onClose();

      // Call onAuthSuccess callback if provided, with a small delay to ensure auth state is updated
      if (onAuthSuccess) {
        // Small delay to ensure React state updates are processed
        setTimeout(() => {
          onAuthSuccess(userData);
        }, 100);
      } else {
        // Redirect based on user type
        if (userData.userType === 'client') {
          // Redirect to client view
          navigate('/client');
        } else {
          // Redirect to pro view based on role
          if (userData.role === 'professional_employe') {
            // Employee: redirect to employee dashboard
            navigate('/pro/employe/dashboard');
          } else {
            // Manager/Owner: redirect to main dashboard
            navigate('/pro/dashboard');
          }
        }
      }
    } catch (error) {
      console.error('AuthModal: Login failed:', error);
      setLoginError(
        extractBackendErrorMessage(error) ||
        t('auth.errors.login_failed')
      );
    } finally {
      setSubmitting(false);
    }
  };

  // Handle register submission
  const handleRegister = async (values, { setSubmitting, resetForm }) => {
    setRegisterError('');

    try {
      const registerRequest = {
        email: values.email,
        password: values.password,
        password_confirm: values.password_confirm,
        first_name: values.first_name,
        last_name: values.last_name,
        phone: values.phone || null
      };

      // Use the register function from AuthContext
      const userData = await register(registerRequest, userType);

      // Show success notification
      setNotification({
        open: true,
        message: t('auth.notifications.register_success_autologin'),
        severity: 'success'
      });

      // Handle successful registration
      resetForm();
      onClose();

      // Call onAuthSuccess callback if provided, with a small delay to ensure auth state is updated
      if (onAuthSuccess) {
        // Small delay to ensure React state updates are processed
        setTimeout(() => {
          onAuthSuccess(userData);
        }, 100);
      } else {
        // Redirect based on user type (same logic as login)
        if (userData.userType === 'client') {
          navigate('/client');
        } else {
          if (userData.role === 'professional_employe') {
            navigate('/pro/employe/dashboard');
          } else {
            navigate('/pro/dashboard');
          }
        }
      }
    } catch (error) {
      console.error('Registration failed:', error);
      setRegisterError(
        extractBackendErrorMessage(error) ||
        t('auth.errors.register_failed')
      );
    } finally {
      setSubmitting(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  // Password visibility icon components
  const passwordVisibilityIcon = (
    <IconButton
      onClick={togglePasswordVisibility}
      edge="end"
      sx={{
        color: theme.colors?.grey?.[600] || '#757575',
        '&:hover': { color: theme.colors?.primary?.[500] || '#416548' }
      }}
    >
      {showPassword ? <VisibilityOff /> : <Visibility />}
    </IconButton>
  );

  const confirmPasswordVisibilityIcon = (
    <IconButton
      onClick={toggleConfirmPasswordVisibility}
      edge="end"
      sx={{
        color: theme.colors?.grey?.[600] || '#757575',
        '&:hover': { color: theme.colors?.primary?.[500] || '#416548' }
      }}
    >
      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
    </IconButton>
  );

  // Custom styles using your theme
  const customStyles = {
    dialog: {
      '& .MuiDialog-paper': {
        borderRadius: 3,
        background: 'linear-gradient(135deg, #f9f9f9 0%, #f0f0f0 100%)',
        boxShadow: '0 10px 40px rgba(0, 0, 0, 0.15)',
        overflow: 'hidden',
        position: 'relative',
        zIndex: 1300, // Ensure it's above other content
        '&:before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 6,
          background: 'linear-gradient(90deg, #dfa574 0%, #e2c29c 100%)',
          zIndex: 1301 // Above the dialog content
        }
      }
    },
    dialogTitle: {
      background: `linear-gradient(135deg, ${theme.colors?.primary?.[500] || '#416548'} 0%, ${theme.colors?.primary?.[600] || '#3a5b41'} 100%)`,
      color: 'white',
      borderRadius: '12px 12px 0 0',
      margin: '-8px -8px 0 -8px',
      padding: '20px 24px',
      position: 'relative',
      '&::after': {
        content: '""',
        position: 'absolute',
        bottom: '-10px',
        left: '50%',
        transform: 'translateX(-50%)',
        width: '60px',
        height: '4px',
        backgroundColor: theme.colors?.secondary?.[300] || '#e2c29c',
        borderRadius: '2px'
      }
    },
    dialogContent: {
      padding: '24px 32px 32px 32px',
      background: 'transparent'
    },
    tabs: {
      marginBottom: 2,
      '& .MuiTabs-root': {
        borderBottom: `2px solid ${theme.colors?.grey?.[200] || '#eeeeee'}`
      },
      '& .MuiTab-root': {
        textTransform: 'none',
        fontSize: '1.1rem',
        fontWeight: 600,
        minWidth: 120,
        padding: '12px 24px',
        color: theme.colors?.grey?.[600] || '#757575',
        transition: 'all 0.3s ease',
        '&:hover': {
          color: theme.colors?.primary?.[500] || '#416548',
          backgroundColor: theme.colors?.primary?.[50] || '#f0f4f1'
        },
        '&.Mui-selected': {
          color: theme.colors?.primary?.[600] || '#3a5b41',
          fontWeight: 700
        }
      },
      '& .MuiTabs-indicator': {
        height: 3,
        borderRadius: '2px 2px 0 0',
        background: `linear-gradient(90deg, ${theme.colors?.primary?.[500] || '#416548'} 0%, ${theme.colors?.secondary?.[300] || '#e2c29c'} 100%)`
      }
    },
    textField: {
      '& .MuiOutlinedInput-root': {
        borderRadius: 2,
        backgroundColor: theme.colors?.grey?.[50] || '#fafafa',
        transition: 'all 0.3s ease',
        '& fieldset': {
          borderColor: theme.colors?.grey?.[300] || '#e0e0e0',
          borderWidth: 1.5
        },
        '&:hover fieldset': {
          borderColor: theme.colors?.primary?.[400] || '#6b8d76'
        },
        '&.Mui-focused fieldset': {
          borderColor: theme.colors?.primary?.[500] || '#416548',
          borderWidth: 2
        },
        '&.Mui-focused': {
          backgroundColor: 'white',
          boxShadow: `0 0 0 3px ${theme.colors?.primary?.[50] || '#f0f4f1'}`
        }
      },
      '& .MuiInputLabel-root': {
        color: theme.colors?.grey?.[600] || '#757575',
        fontWeight: 500,
        '&.Mui-focused': {
          color: theme.colors?.primary?.[600] || '#3a5b41'
        }
      }
    },
    primaryButton: {
      textTransform: 'none',
      borderRadius: 3,
      padding: '12px 32px',
      fontSize: '1rem',
      fontWeight: 600,
      background: `linear-gradient(135deg, ${theme.colors?.primary?.[500] || '#416548'} 0%, ${theme.colors?.primary?.[600] || '#3a5b41'} 100%)`,
      boxShadow: '0 4px 12px rgba(65, 101, 72, 0.3)',
      transition: 'all 0.3s ease',
      '&:hover': {
        background: `linear-gradient(135deg, ${theme.colors?.primary?.[600] || '#3a5b41'} 0%, ${theme.colors?.primary?.[700] || '#2f4a36'} 100%)`,
        boxShadow: '0 6px 20px rgba(65, 101, 72, 0.4)',
        transform: 'translateY(-1px)'
      },
      '&:disabled': {
        background: theme.colors?.grey?.[300] || '#e0e0e0',
        color: theme.colors?.grey?.[500] || '#8c8c8c',
        boxShadow: 'none'
      }
    },
    secondaryButton: {
      textTransform: 'none',
      color: theme.colors?.primary?.[600] || '#3a5b41',
      fontWeight: 500,
      padding: '8px 16px',
      '&:hover': {
        backgroundColor: theme.colors?.primary?.[50] || '#f0f4f1',
        color: theme.colors?.primary?.[700] || '#2f4a36'
      }
    },
    formContainer: {
      backgroundColor: 'white',
      borderRadius: 2,
      padding: '24px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
      border: `1px solid ${theme.colors?.grey?.[200] || '#eeeeee'}`
    },
    alert: {
      borderRadius: 2,
      '&.MuiAlert-standardError': {
        backgroundColor: '#fef2f2',
        color: '#991b1b',
        border: '1px solid #fecaca',
        '& .MuiAlert-icon': {
          color: '#dc2626'
        }
      }
    }
  };

  const handleGoogleCredential = async (credentialResponse) => {
    try {
      const idToken = credentialResponse?.credential;
      if (!idToken) {
        setLoginError(t('auth.errors.google_login_failed'));
        return;
      }

      const userData = await googleLogin({ idToken }, userType);
      onClose();

      if (onAuthSuccess) {
        setTimeout(() => {
          onAuthSuccess(userData);
        }, 100);
      } else {
        if (userData.userType === 'client') {
          navigate('/client');
        } else {
          if (userData.role === 'professional_employe') {
            navigate('/pro/employe/dashboard');
          } else {
            navigate('/pro/dashboard');
          }
        }
      }
    } catch (error) {
      console.error('AuthModal: Google login failed:', error);
      const detail = error?.response?.data?.detail;
      setLoginError(detail || t('auth.errors.google_login_failed'));
    }
  };

  const handleGoogleLinkCredential = async (credentialResponse) => {
    try {
      const idToken = credentialResponse?.credential;
      if (!idToken) {
        setNotification({ open: true, message: t('auth.errors.google_link_failed'), severity: 'error' });
        return;
      }
      await googleLink({ idToken });
      setNotification({ open: true, message: t('auth.notifications.google_link_success'), severity: 'success' });
    } catch (error) {
      const detail = error?.response?.data?.detail;
      setNotification({ open: true, message: detail || t('auth.errors.google_link_failed'), severity: 'error' });
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        sx={{
          ...customStyles.dialog,
          '& .MuiDialog-container': {
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100%',
            '& .MuiPaper-root': {
              margin: '16px',
              width: '100%',
              maxWidth: '500px',
              maxHeight: '90vh',
              overflowY: 'auto',
              position: 'relative',
              zIndex: 1300 // Ensure it's above other content
            }
          }
        }}
      >
        <DialogTitle sx={customStyles.dialogTitle}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h5" component="div" sx={{ fontWeight: 700, fontSize: '1.5rem' }}>
              {userType === 'client' ? t('auth.title.client') : t('auth.title.professional')}
            </Typography>

            <IconButton
              onClick={onClose}
              aria-label="close"
              sx={{
                color: 'white',
                '&:hover': {
                  backgroundColor: 'rgba(255,255,255,0.1)',
                  transform: 'scale(1.1)'
                }
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={customStyles.dialogContent}>
          <Snackbar
            open={notification.open}
            autoHideDuration={3500}
            onClose={handleCloseNotification}
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            sx={{
              '&.MuiSnackbar-root': {
                position: 'relative',
                top: 0,
                zIndex: 1401,
              }
            }}
          >
            <Alert
              onClose={handleCloseNotification}
              severity={notification.severity}
              sx={{ width: '100%' }}
            >
              {notification.message}
            </Alert>
          </Snackbar>

          <Divider sx={{ mb: 3 }} />

          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            aria-label="authentication tabs"
            textColor="primary"
            indicatorColor="primary"
            centered
          >
            <Tab label={t('auth.tabs.login')} {...a11yProps(0)} />
            {professionalRegistrationAllowed && (
              <Tab label={t('auth.tabs.register')} {...a11yProps(1)} />
            )}
          </Tabs>

          {/* Login Panel */}
          <TabPanel value={tabValue} index={0}>
            <Box sx={customStyles.formContainer}>
              <Formik
                initialValues={loginInitialValues}
                validationSchema={loginSchema}
                onSubmit={handleLogin}
              >
                {({ isSubmitting }) => (
                  <Form style={{ width: '100%' }}>
                    {loginError && (
                      <Alert severity="error" sx={{ ...customStyles.alert, mb: 3 }}>
                        {loginError}
                      </Alert>
                    )}

                    <FormInput
                      name="email"
                      label={t('auth.fields.email')}
                      type="email"
                      autoComplete="email"
                      autoFocus
                      placeholder="exemple@email.com"
                      sx={{ ...customStyles.textField, mb: 3 }}
                      icon={<EmailIcon color="action" />}
                    />

                    <FormInput
                      name="password"
                      label={t('auth.fields.password')}
                      type={showPassword ? 'text' : 'password'}
                      autoComplete="current-password"
                      placeholder={t('auth.placeholders.password')}
                      sx={{ ...customStyles.textField, mb: 2 }}
                      icon={passwordVisibilityIcon}
                    />

                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                      <Button
                        onClick={() => {
                          onClose();
                          navigate('/request-password-reset');
                        }}
                        sx={{
                          color: theme.colors.primary[500],
                          textTransform: 'none',
                          '&:hover': {
                            backgroundColor: 'transparent',
                            textDecoration: 'underline'
                          }
                        }}
                      >
                        {t('auth.actions.forgot_password')}
                      </Button>
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 2 }}>

                      <Button
                        type="submit"
                        variant="contained"
                        disabled={isSubmitting || isLoading}
                        sx={customStyles.primaryButton}
                      >
                        {(isSubmitting || isLoading) ? <CircularProgress size={24} color="inherit" /> : t('auth.actions.login')}
                      </Button>
                    </Box>
                  </Form>
                )}
              </Formik>
            </Box>
          </TabPanel>

          {/* Register Panel */}
          {professionalRegistrationAllowed && (
            <TabPanel value={tabValue} index={1}>
            <Box sx={customStyles.formContainer}>
              <Formik
                initialValues={registerInitialValues}
                validationSchema={registerSchema}
                onSubmit={handleRegister}
                validateOnChange={true}
                validateOnBlur={true}
              >
                {({ isSubmitting }) => (
                  <Form style={{ width: '100%' }}>
                    {registerError && (
                      <Alert severity="error" sx={{ ...customStyles.alert, mb: 3 }}>
                        {registerError}
                      </Alert>
                    )}

                    <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                      <FormInput
                        name="first_name"
                        label={t('auth.fields.first_name')}
                        placeholder="Jean"
                        sx={customStyles.textField}
                        icon={<PersonIcon color="action" />}
                      />

                      <FormInput
                        name="last_name"
                        label={t('auth.fields.last_name')}
                        placeholder="Dupont"
                        sx={customStyles.textField}
                        icon={<PersonIcon color="action" />}
                      />
                    </Box>

                    <FormInput
                      name="email"
                      label={t('auth.fields.email')}
                      type="email"
                      autoComplete="email"
                      placeholder="exemple@email.com"
                      sx={{ ...customStyles.textField, mb: 2 }}
                      icon={<EmailIcon color="action" />}
                      validateOnChange
                      validateOnBlur
                    />

                    <FormInput
                      name="phone"
                      label={t('auth.fields.phone')}
                      autoComplete="tel"
                      placeholder="+33 6 12 34 56 78"
                      sx={{ ...customStyles.textField, mb: 2 }}
                      icon={<PhoneIcon color="action" />}
                      validateOnChange
                      validateOnBlur
                      inputProps={{
                        pattern: '[+]?[0-9]{1,15}',
                        inputMode: 'tel'
                      }}
                    />

                    <FormInput
                      name="password"
                      label={t('auth.fields.password')}
                      type={showPassword ? 'text' : 'password'}
                      autoComplete="new-password"
                      placeholder={t('auth.placeholders.password_min')}
                      sx={{ ...customStyles.textField, mb: 3 }}
                      icon={passwordVisibilityIcon}
                    />

                    <FormInput
                      name="password_confirm"
                      label={t('auth.fields.password_confirm')}
                      type={showConfirmPassword ? 'text' : 'password'}
                      autoComplete="new-password"
                      placeholder={t('auth.placeholders.password_confirm')}
                      sx={{ ...customStyles.textField, mb: 4 }}
                      icon={confirmPasswordVisibilityIcon}
                    />

                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                      <Button
                        type="submit"
                        variant="contained"
                        disabled={isSubmitting || isLoading}
                        sx={customStyles.primaryButton}
                      >
                        {(isSubmitting || isLoading) ? <CircularProgress size={24} color="inherit" /> : t('auth.actions.register')}
                      </Button>
                    </Box>
                  </Form>
                )}
              </Formik>
            </Box>
            </TabPanel>
          )}

          {!nativeIosApp ? (
            <>
              <Divider sx={{ my: 3 }} />

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {shouldShowGoogleLogin ? (
                  <>
                    <GoogleLogin
                      onSuccess={handleGoogleCredential}
                      onError={() => setLoginError(t('auth.errors.google_login_failed'))}
                    />

                    {user ? (
                      <GoogleLogin
                        onSuccess={handleGoogleLinkCredential}
                        onError={() => setNotification({ open: true, message: t('auth.errors.google_link_failed'), severity: 'error' })}
                        text="continue_with"
                      />
                    ) : null}
                  </>
                ) : (
                  <Alert severity="warning" sx={customStyles.alert}>
                    {t('auth.errors.google_unavailable')}
                  </Alert>
                )}
              </Box>
            </>
          ) : null}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AuthModal;
